interface ApiErrorBody {
  error?: { code?: string; message?: string; request_id?: string };
}

export function getApiBaseUrl(): string | null {
  const value = import.meta.env.VITE_API_BASE_URL?.trim().replace(/\/$/, '');
  return value || null;
}

export function hasApiConfig(): boolean {
  return !!getApiBaseUrl();
}

// 결과를 알 수 없이 끝난 쓰기 요청(네트워크 오류, 5xx, 처리 중)은 같은 요청을 다시 보낼 때
// 같은 Idempotency-Key를 재사용해야 서버가 중복 반영을 막을 수 있다. 확정 응답을 받으면 키를 버린다.
const PENDING_KEY_TTL_MS = 12 * 60 * 60 * 1000;
const pendingIdempotencyKeys = new Map<string, { key: string; createdAt: number }>();

function idempotencyKeyFor(fingerprint: string): string {
  const now = Date.now();
  const pending = pendingIdempotencyKeys.get(fingerprint);
  if (pending && now - pending.createdAt < PENDING_KEY_TTL_MS) return pending.key;
  const key = crypto.randomUUID();
  pendingIdempotencyKeys.set(fingerprint, { key, createdAt: now });
  return key;
}

function isAmbiguousOutcome(status: number, code: string | undefined): boolean {
  return status >= 500 || code === 'IDEMPOTENCY_IN_PROGRESS';
}

export async function apiRequest<T>(
  path: string,
  init: RequestInit & { idempotent?: boolean } = {},
): Promise<T> {
  const baseUrl = getApiBaseUrl();
  if (!baseUrl) throw new Error('VITE_API_BASE_URL이 설정되지 않았습니다.');

  const headers = new Headers(init.headers);
  if (init.body !== undefined) headers.set('Content-Type', 'application/json');
  const fingerprint = init.idempotent
    ? `${init.method ?? 'GET'} ${path} ${typeof init.body === 'string' ? init.body : ''}`
    : null;
  if (fingerprint) headers.set('Idempotency-Key', idempotencyKeyFor(fingerprint));

  // fetch 자체가 실패하면 요청이 서버에 닿았는지 알 수 없으므로 키를 그대로 남겨 둔다.
  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });

  const body = response.status === 204
    ? null
    : ((await response.json().catch(() => null)) as T | ApiErrorBody | null);
  const errorCode = (body as ApiErrorBody | null)?.error?.code;
  if (fingerprint && !isAmbiguousOutcome(response.status, errorCode)) {
    pendingIdempotencyKeys.delete(fingerprint);
  }
  if (response.status === 204) return undefined as T;

  if (!response.ok) {
    const error = body as ApiErrorBody | null;
    throw new Error(error?.error?.message ?? 'API 요청을 처리하지 못했습니다.');
  }
  return body as T;
}

export function queryString(values: Record<string, string | number | boolean | null | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}
